import React, { useContext, useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { useMutation } from '@apollo/client';
import DataTable from '@preflighttech/preflight-tables';
import Filter from 'app/src/elements/Filter';
import { ResetSettings } from 'app/src/elements/Pagination/ResetSettings';

import AuthContext from 'app/src/contexts/AuthContext';
import {
  MainContainer,
  ContentContainer,
  PageTitle,
  LinkContainer,
  LinkSpace,
} from 'app/src/elements';
import Modal from 'app/src/elements/Modal';
import { dateShort } from 'app/src/utils/timeFormatter';

import { TXNS_WITH_COUNT } from './queries';
import SubNav from './SubNav';
import EditTxn from './edit';
import PrintCheck from './PrintCheck';
import TYPES from './types';

const baseColumns = [
  {
    key: 'bankDate',
    label: 'Date',
    sort: 'desc',
    content: dateShort,
  },
  {
    key: 'type',
    label: 'Type',
  },
  {
    key: 'method',
    label: 'Method',
  },
  {
    key: 'reference',
    label: 'Reference',
  },
  {
    key: 'name',
    label: 'Name',
  },
];

const Txns = () => {
  const [showEditModal, setShowEditModal] = useState();
  const [showPrintModal, setShowPrintModal] = useState();

  const [refetch, setRefetch] = useState();
  const [filterOptions, setFilterOptions] = useState();
  const [filters, setFilters] = useState({});
  const [settings, setSettings] = useState({});
  const queryVariables = {
    filterColumns: [],
    filterNames: [],
    scopes: [],
    order: [{key: 'bankDate', direction: 'desc'}],
  };

  const setFilter = (key, value) => {
    const currentFilters = {...filters};
    currentFilters[key] = value;
    setFilters(currentFilters);
  }

  Object.entries(filters).forEach(([key, value]) => {
    if (value) {
      queryVariables.filterColumns.push(key);
      queryVariables.filterNames.push(value);
    }
  });

  const { accounts, role } = useContext(AuthContext);

  const columns = [
    ...baseColumns,
    {
      key: 'functions',
      label: 'Functions',
      sort: 'prevent',
      content: ({ entry }) => (
        <LinkContainer style={{ gap: 20 }}>
          <Pressable onPress={() => setShowEditModal(entry)}>
            <Text>Edit</Text>
          </Pressable>

          <Pressable onPress={() => setShowPrintModal(entry)}>
            <Text>Print Check</Text>
          </Pressable>
        </LinkContainer>
      )
    },
  ];

  const buttons = (
    <View style={{display: 'flex', flexDirection: 'row'}}>
      <Filter
        clearFilters={() => {
          setFilters({})
          setShowModal(false)
        }}
        filters={filters}
        setFilters={setFilters}
      >
        <View style={{padding: '20px'}}>
          <View style={{marginBottom: '20px'}}>
            <Text>Type:</Text>
            <select
              type={'select'}
              name="type"
              onChange={e => setFilter('type', e.target.value)}
              value={filters.types || ''}
            >
              <option key={`type-any`}>{'Any'}</option>
              {TYPES.map(type => <option key={`type-${type}`}>{type}</option>)}
            </select>
          </View>
          <View style={{marginBottom: '20px'}}>
            <Filter.DateRange
              columnKey="bank_date"
              label="Date"
              filters={filters}
              setFilter={setFilter}
            />
          </View>
          <Text>Account:</Text>
          <select
            type={'select'}
            name="account_id"
            onChange={e => {
              setFilter('account_id', e.target.value)}
            }
            value={filters.account_id || ''}
          >
            <option key={`account-any`}>{'Any'}</option>
            {(accounts || []).map(account => (
              <option key={account.id} value={account.id}>
                {account.label}
              </option>
            ))}
          </select>
        </View>
      </Filter>
      <ResetSettings />
    </View>
  );

  return (
    <MainContainer>
      <SubNav refetch={refetch} />

      <ContentContainer>
        <PageTitle>Transaction Register</PageTitle>

        <DataTable.Gql
          setRefetch={setRefetch}
          query={TXNS_WITH_COUNT}
          queryName='txnsWithCount'
          queryVariables={queryVariables}
          columns={columns}
          pageLength={25}
          buttons={buttons}
          lengthMenu={ [10, 25, 50, 'All'] }
          setSettings={setSettings}
          dataUpdated={data => setFilterOptions(data?.txnsWithCount)}
          htmlTable
        />
      </ContentContainer>

      {showEditModal &&
        <Modal
          showModal
          setShowModal={setShowEditModal}
          modalType="Edit Transaction"
        >
          <EditTxn
            txn={showEditModal}
            setShowModal={setShowEditModal}
            refetch={refetch}
          />
        </Modal>
      }

      {showPrintModal &&
        <Modal
          showModal
          setShowModal={setShowPrintModal}
          modalType="Print Check"
        >
          <PrintCheck
            txn={showPrintModal}
            setShowModal={setShowPrintModal}
          />
        </Modal>
      }
    </MainContainer>
  );
};

export default Txns;
